import React, { useState, useEffect } from 'react';
import { Modal, Button, Nav, Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { createUserMedicalHistory } from 'apiServices/reducer/dialogApiService';
import '../../../styles/user.css'; 
import CustomSuccessModal from '../../../components/CustomSuccessModal'; 

const ManageMedicalHistory = ({ show, onHide, medicalData, refreshData, userId }) => {
    const { register, handleSubmit, reset, formState: { errors } } = useForm();
    const [activeTab, setActiveTab] = useState('medical');
    const dispatch = useDispatch();
    const token = useSelector((state) => state.apiService?.token);  
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [fieldErrors, setFieldErrors] = useState({ medical_history: false, surgical_history: false, dental_history: false });
    const inputErrorStyle = { border: '1px solid #dc3545', boxShadow: 'none' };

    useEffect(() => {
        if (show && medicalData) {
            reset({
                medical_history: medicalData.medical_history || '',
                surgical_history: medicalData.surgical_history || '',
                dental_history: medicalData.dental_history || ''
            });
        } else {
            reset({
                medical_history: '',
                surgical_history: '',
                dental_history: ''
            });
        }
        setFieldErrors({ medical_history: false, surgical_history: false, dental_history: false });
    }, [show, medicalData, reset]);

    const onSubmit = async (data) => {
        // Check if at least one field has data
        const hasData = data.medical_history?.trim() || data.surgical_history?.trim() || data.dental_history?.trim();
        
        if (!hasData) { 
            setFieldErrors({ medical_history: true, surgical_history: true, dental_history: true });
            return;
        }

        setFieldErrors({ medical_history: false, surgical_history: false, dental_history: false });
        const formBody = {
            medical_history: data.medical_history || '',
            surgical_history: data.surgical_history || '',
            dental_history: data.dental_history || '',
            user_id: userId,
            token
        };

        try {
            const response = await dispatch(createUserMedicalHistory(formBody)).unwrap();
            setSuccessMessage(response.OutputMessage || 'Medical history updated successfully!');
            setShowSuccessModal(true);
            refreshData();
            onHide();
        } catch (error) {
            console.error('Error updating medical history:', error);
          
        }
    };

    return (
        <Modal show={show} onHide={onHide} dialogClassName="medical-history-modal" backdrop="static" keyboard={false} onMouseDown={(e) => e.stopPropagation()}>
            <Modal.Header closeButton className='modal-header-bg'>
                <Modal.Title className='text-capitalize'>Edit Medical History</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Nav variant="tabs" defaultActiveKey="medical" className="mb-3 custom-tabs">
                    <Nav.Item>
                        <Nav.Link
                            eventKey="medical"
                            active={activeTab === 'medical'}
                            onClick={() => setActiveTab('medical')}
                        >
                            Medical History
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link
                            eventKey="surgical"
                            active={activeTab === 'surgical'}
                            onClick={() => setActiveTab('surgical')}
                        >
                            Surgical History
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link
                            eventKey="dental"
                            active={activeTab === 'dental'}
                            onClick={() => setActiveTab('dental')}
                        >
                            Dental History
                        </Nav.Link>
                    </Nav.Item>
                </Nav>

                <Form onSubmit={handleSubmit(onSubmit)}>
                    {activeTab === 'medical' && (
                        <Form.Group controlId="medicalHistory">
                            <Form.Control
                                as="textarea"
                                rows={15}
                                columns={30}
                                {...register('medical_history')}
                                placeholder="Enter medical history"
                                className={`form-control custom-textarea ${fieldErrors.medical_history ? 'error-border' : ''}`}
                                style={fieldErrors.medical_history ? inputErrorStyle : {}}
                                onChange={(e) => {
                                    if (e.target.value.trim()) {
                                        setFieldErrors((prev) => ({ ...prev, medical_history: false }));
                                    }
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                            />
                        </Form.Group>
                    )}

                    {activeTab === 'surgical' && (
                        <Form.Group controlId="surgicalHistory">
                            <Form.Control
                                as="textarea"
                                rows={15}
                                columns={30}
                                {...register('surgical_history')}
                                placeholder="Enter surgical history"
                                className={`form-control custom-textarea ${fieldErrors.surgical_history ? 'error-border' : ''}`}
                                style={fieldErrors.surgical_history ? inputErrorStyle : {}}
                                onChange={(e) => {
                                    if (e.target.value.trim()) {
                                        setFieldErrors((prev) => ({ ...prev, surgical_history: false }));
                                    }
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                            />
                        </Form.Group>
                    )}

                    {activeTab === 'dental' && (
                        <Form.Group controlId="dentalHistory">
                            <Form.Control
                                as="textarea"
                                rows={15}
                                columns={30}
                                {...register('dental_history')}
                                placeholder="Enter dental history"
                                className={`form-control custom-textarea ${fieldErrors.dental_history ? 'error-border' : ''}`}
                                style={fieldErrors.dental_history ? inputErrorStyle : {}}
                                onChange={(e) => {
                                    if (e.target.value.trim()) {
                                        setFieldErrors((prev) => ({ ...prev, dental_history: false }));
                                    }
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                            />
                        </Form.Group>
                    )}
                </Form>
            </Modal.Body>
            <Modal.Footer className='modal-header-bg'>
                <Button variant="primary" className="me-2" onClick={handleSubmit(onSubmit)}>Update Changes</Button>
                <Button variant="danger" className="ms-2" onClick={onHide}>Close</Button>
            </Modal.Footer> 
            <CustomSuccessModal
                show={showSuccessModal}
                onHide={() => { setShowSuccessModal(false); setSuccessMessage(''); }}
                message={successMessage}
            /> 
            <style>{`.error-border { border: 1px solid #dc3545 !important; box-shadow: none !important; }`}</style>
        </Modal>
    );
};

export default ManageMedicalHistory;